import React, { useEffect, useRef } from 'react';

// the store module only: the feature barrel also carries the flow's dialogs,
// and the sign-out one imports the logout routine.
import { useFlowStore } from '@/features/flow/store';
import { fetchOAuthMe } from '@/lib/api/auth';
import type { MeResponse } from '@/lib/api/auth';

import { useOAuthSession } from './hooks/useOAuthSession';
import { consumeResumeLogin } from './loopbackOAuthHost';
import {
    shouldRequestOAuthBinding,
    useOAuthBindingDialogStore,
} from './oauthBindingDialogStore';
import { useOAuthStore } from './store';

/** Path that owns the OAuth handshake; it hydrates the session itself. */
const CALLBACK_PATH = '/auth/callback';

/**
 * Hydrates the social-login session once, on app boot, via `GET /v2/auth/me`.
 *
 * Mounted high in the tree so the result is shared by every `useOAuthSession()`
 * consumer. Skips hydration on the OAuth callback route — that page completes
 * the handshake and seeds the session itself, so an early `me` fetch would just
 * race it.
 */
export const OAuthProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const setMe = useOAuthStore((s) => s.setMe);
    const setInitialized = useOAuthStore((s) => s.setInitialized);
    const { login } = useOAuthSession();

    // auto-resume a login that bounced across loopback hosts (dev only — see
    // loopbackOAuthHost.ts): the resume param is stripped BEFORE the login
    // restarts, so a failing login can never loop the bounce.
    const resumeStarted = useRef(false);
    useEffect(() => {
        if (resumeStarted.current) return;
        resumeStarted.current = true;
        if (window.location.pathname.startsWith(CALLBACK_PATH)) return;
        const provider = consumeResumeLogin();
        if (provider) {
            void login(provider).catch((error) => {
                console.error(
                    '[OAuthProvider] resumed loopback login failed',
                    error,
                );
            });
        }
    }, [login]);

    // guards the one-shot hydration WITHOUT going through react state. `initialized` used to do this
    // job while also sitting in the dependency array, so `setInitialized(true)` re-triggered the
    // effect, react tore the previous run down, and its cleanup flipped `cancelled` before the fetch
    // resolved — the response was fetched and then thrown away, leaving `me` null forever. the header
    // fell back to "Signed in" and the account panel to "OAuth account" on every reload, while
    // /v2/auth/me was answering 200 with the full session all along. it only looked fine right after
    // login because OAuthCallbackPage seeds the store itself.
    const hydrationStarted = useRef(false);

    useEffect(() => {
        if (hydrationStarted.current) return;
        hydrationStarted.current = true;
        setInitialized(true);

        if (window.location.pathname.startsWith(CALLBACK_PATH)) {
            return;
        }

        void (async () => {
            let me: MeResponse;
            try {
                me = await fetchOAuthMe();
            } catch (error) {
                // a FAILED fetch is not proof of being logged out, but the store has no third state
                // today; surface it so it is never again silently indistinguishable from a real logout.
                console.error(
                    '[OAuthProvider] /v2/auth/me hydration failed',
                    error,
                );
                me = { authenticated: false };
            }
            // deliberately NOT gated on an unmount flag: this writes to an external zustand store,
            // which is safe after unmount, and any such gate reintroduces the discarded-response
            // bug the moment the effect is torn down early (react StrictMode does exactly that).
            setMe(me);
            // which flow (V1 custodial / V2 browser wallet) this session is in. Without a social
            // session it is decided on the spot, with no request; with one it costs two reads (5 s
            // cap each, a failure means V1). Everything below is the V1 post-login path, unchanged
            // apart from waiting for this.
            const flow = await useFlowStore.getState().resolve(me);
            if (
                flow === 'v1' &&
                me.authenticated &&
                shouldRequestOAuthBinding(me)
            ) {
                useOAuthBindingDialogStore.getState().request({
                    provider: me.provider,
                    accountHash: me.accountHash,
                });
            }
        })();
    }, [setMe, setInitialized]);

    return <>{children}</>;
};

export default OAuthProvider;
